/**
 * imageSrc.js
 * ───────────
 * Responsive image helpers for blocks. Only images served through the
 * site's /media/ proxy (or its S3 origin) can be resized; every other URL
 * gets lazy loading and dimensions only.
 *
 *   isProxied(url)              → boolean
 *   variantUrl(url, w, opts)    → url with ?w=&f=auto (or the url unchanged)
 *   buildSrcSet(url, widths)    → "…?w=320&f=auto 320w, …" or ''
 *   imgAttrs(url, { sizes, priority, widths })
 *       → { src, srcset, sizes, loading, decoding, fetchpriority }
 *
 * The width ladder matches ImageVariantService::LADDER on the server.
 */
export var LADDER = [320, 640, 960, 1280, 1920, 2560];

var S3_BASE = 'https://prodaccountfiles.s3.us-east-1.amazonaws.com/';

/**
 * Origin that serves /media/ (the public website). Empty on the public site
 * itself, where relative URLs work. Admin screens (page editor, menu manager)
 * set it to the website's temp/preview URL because the admin host has no
 * /media/ proxy.
 */
var originState = { origin: '' };
try {
	// Reactive when Vue is available so computed image attributes re-run once the origin is known
	// (admin screens learn the website URL after the first blocks have rendered).
	var VueLib = require('vue');
	VueLib = VueLib && VueLib.default ? VueLib.default : VueLib;
	if (VueLib && typeof VueLib.observable === 'function') originState = VueLib.observable(originState);
} catch (e) { /* no Vue (tests, plain scripts): plain object */ }
export function setMediaOrigin(origin) {
	originState.origin = origin ? String(origin).replace(/\/+$/, '') : '';
}
export function getMediaOrigin() { return originState.origin; }

export function isProxied(url) {
	var u = String(url || '');
	return u.indexOf('/media/') === 0 || u.indexOf(S3_BASE) === 0 || /^https?:\/\/[^/]+\/media\//.test(u);
}

/** S3 origin URLs become proxy URLs so the variant params can apply. */
export function toProxy(url) {
	var u = String(url || '');
	if (u.indexOf(S3_BASE) === 0) u = '/media/' + u.substring(S3_BASE.length);
	if (originState.origin && u.indexOf('/media/') === 0) u = originState.origin + u;
	return u;
}

export function variantUrl(url, width, opts) {
	if (!isProxied(url)) return url;
	var base = toProxy(url).split('?')[0];
	var params = ['w=' + width];
	if (!opts || opts.format !== false) params.push('f=auto');
	if (opts && opts.quality) params.push('q=' + opts.quality);
	return base + '?' + params.join('&');
}

export function buildSrcSet(url, widths) {
	if (!isProxied(url)) return '';
	return (widths || LADDER).map(function (w) { return variantUrl(url, w) + ' ' + w + 'w'; }).join(', ');
}

/**
 * Attribute bag for an <img>. `priority` marks above-the-fold images: no
 * lazy loading and fetchpriority=high. `sizes` defaults to the full width.
 * `candidates` ([{ url, w }]) describes ready-made copies of a picture that
 * is not on the media proxy (for example the WordPress thumbnail / large /
 * full files of a synced post); they become the srcset instead.
 */
export function imgAttrs(url, opts) {
	opts = opts || {};
	var proxied = isProxied(url);
	var attrs = {
		src: proxied ? variantUrl(url, opts.fallbackWidth || 1280) : url,
		loading: opts.priority ? 'eager' : 'lazy',
		decoding: 'async'
	};
	if (proxied) {
		attrs.srcset = buildSrcSet(url, opts.widths);
		attrs.sizes = opts.sizes || '100vw';
	} else if (opts.candidates && opts.candidates.length > 1) {
		var seen = {};
		var list = opts.candidates.filter(function (c) { if (!c || !c.url || !c.w || seen[c.url]) return false; seen[c.url] = true; return true; });
		if (list.length > 1) {
			attrs.srcset = list.map(function (c) { return c.url + ' ' + c.w + 'w'; }).join(', ');
			attrs.sizes = opts.sizes || '100vw';
		}
	}
	if (opts.priority) attrs.fetchpriority = 'high';
	return attrs;
}

/**
 * Width of a WordPress sized copy from its file name (…-1024x683.jpg → 1024).
 * The original has no suffix: `fallback` stands in (it is the largest copy).
 */
export function wpSizedWidth(url, fallback) {
	var m = String(url || '').match(/-(\d{2,5})x(\d{2,5})(?:\.[a-z0-9]{2,5})?(?:[?#].*)?$/i);
	return m ? parseInt(m[1], 10) : fallback;
}

/** CSS background-image value for a sized variant (used by Post List tiles). */
export function bgImage(url, width) {
	return 'url("' + String(variantUrl(url, width || 960)).replace(/"/g, '%22') + '")';
}
